import React from "react";
import { CallBackProps, Step } from "react-joyride";
import OwLabel from "../components/shared/OwLabel/OwLabel";
import IUserUI from "../models/IUserUI";
import UserTypesEnum from "../models/UserTypesEnum";

export default class TutorialService extends React.Component {
    static getCatalogsTutorial = (): Step[] => {
        const steps: Step[] = [
            {
                target: "#create",
                content: <OwLabel title={"tutorial_create_a_catalog"} />,
            },
            {
                target: ".OwActionBar legend",
                content: <OwLabel title={"tutorial_bulk_manage_catalog_with_csv"} />,
            },
            {
                target: 'input[type="checkbox"]:first-of-type',
                content: <OwLabel title={"tutorial_click_checkbox_to_enable_csv_buttons"} />,
            },
            {
                target: ".edit:first-of-type",
                content: <OwLabel title={"tutorial_edit_catalog"} />,
            },
            {
                target: ".view:first-of-type",
                content: <OwLabel title={"tutorial_view_catalog"} />,
            },
            {
                target: ".upload:first-of-type",
                content: <OwLabel title={"tutorial_upload_catalog_update"} />,
                placement: "bottom-end",
            },
        ];

        return steps;
    };

    static getDashboardTutorial = (): Step[] => {
        const steps: Step[] = [
            {
                target: "#menu",
                content: <OwLabel title={"tutorial_dashboard_menu"} />,
            },
            {
                target: 'a[href="/orders?create=true"]',
                content: <OwLabel title={"tutorial_dashboard_create_orders"} />,
            },
            {
                target: ".OwThemeModeSwitcher",
                content: <OwLabel title={"tutorial_dashboard_toggle_theme"} />,
            },
            {
                target: "#logout",
                content: <OwLabel title={"tutorial_dashboard_logout"} />,
                placement: "bottom-end",
            },
        ];

        return steps;
    };

    static getCatalogOverviewTutorial = (): Step[] => {
        const steps: Step[] = [
            {
                target: "#create",
                content: <OwLabel title={"tutorial_catalog_overview_create"} />,
            },
            {
                target: "#price_groups",
                content: <OwLabel title={"tutorial_catalog_overview_price_groups"} />,
            },
            {
                target: "#categories",
                content: <OwLabel title={"tutorial_catalog_overview_categories"} />,
            },
            {
                target: "#order_row_config",
                content: <OwLabel title={"tutorial_catalog_overview_order_row_config"} />,
            },
            {
                target: "#inventory_row_config",
                content: <OwLabel title={"tutorial_catalog_overview_inventory_row_config"} />,
            },
            {
                target: ".open-item-sidebar",
                content: <OwLabel title={"tutorial_catalog_overview_open_item_sidebar"} />,
            },
            {
                target: ".item-has-image",
                content: <OwLabel title={"tutorial_catalog_overview_item_has_image"} />,
            },
            {
                target: ".item-is-product-group",
                content: <OwLabel title={"tutorial_catalog_overview_item_is_product_group"} />,
            },
            {
                target: ".item-has-spec-file",
                content: <OwLabel title={"tutorial_catalog_overview_item_has_spec_file"} />,
            },
            {
                target: ".item-has-inventory",
                content: <OwLabel title={"tutorial_catalog_overview_item_has_inventory"} />,
            },
            {
                target: ".table-column-select",
                content: <OwLabel title={"tutorial_catalog_overview_table_column_select"} />,
                placement: "bottom-end",
            },
        ];

        return steps;
    };

    static getOrdersTutorial = (): Step[] => {
        const steps: Step[] = [
            {
                target: 'button[title="create order"]',
                content: <OwLabel title={"tutorial_orders_create"} />,
            },
            {
                target: ".order-info-icons",
                content: <OwLabel title={"tutorial_orders_info_icons"} />,
            },
            {
                target: '.order-info-icons input[type="checkbox"]',
                content: <OwLabel title={"tutorial_orders_row_checkbox"} />,
            },
            {
                target: 'a[title="Order Entry"]',
                content: <OwLabel title={"tutorial_orders_view_order_entry"} />,
            },
            {
                target: ".table-column-select",
                content: <OwLabel title={"tutorial_orders_table_column_select"} />,
                placement: "bottom-end",
            },
        ];

        return steps;
    };

    static getOrderEntryTutorial = (): Step[] => {
        const steps: Step[] = [
            {
                target: ".OrderProgressTracker",
                content: <OwLabel title={"tutorial_order_entry_progress_tracker_steps"} />,
            },
            {
                target: "#submitButton",
                content: <OwLabel title={"tutorial_order_entry_submit_button"} />,
                placement: "bottom-end",
            },
            {
                target: "#editDetails",
                content: <OwLabel title={"tutorial_order_entry_edit_details"} />,
            },
            {
                target: "#orderDetails",
                content: <OwLabel title={"tutorial_order_entry_order_details"} />,
            },
            {
                target: "#shippingDetails",
                content: <OwLabel title={"tutorial_order_entry_shipping_details"} />,
            },
            {
                target: "#catalogItemLookup",
                content: <OwLabel title={"tutorial_order_entry_catalog_item_lookup"} />,
            },
            {
                target: "#catalogViewer",
                content: <OwLabel title={"tutorial_order_entry_catalog_viewer"} />,
            },
            {
                target: "#categoryViewer",
                content: <OwLabel title={"tutorial_order_entry_category_viewer"} />,
            },
            {
                target: "#itemLookup",
                content: <OwLabel title={"tutorial_order_entry_item_lookup"} />,
            },
            {
                target: "#itemQty",
                content: <OwLabel title={"tutorial_order_entry_item_qty"} />,
            },
            {
                target: "#itemAdd",
                content: <OwLabel title={"tutorial_order_entry_item_add"} />,
            },
            {
                target: "#orderItemTable",
                content: <OwLabel title={"tutorial_order_entry_item_table"} />,
            },
            {
                target: ".itemRowActions",
                content: <OwLabel title={"tutorial_order_entry_item_row_actions"} />,
            },
            {
                target: "#orderTotalsBox",
                content: <OwLabel title={"tutorial_order_entry_order_totals"} />,
            },
        ];

        return steps;
    };

    static getShipmentsTutorial = (userModel?: IUserUI): Step[] => {
        const steps: Step[] = [];

        if (userModel?.isSupplierUser) {
            if (userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN) {
                steps.push({
                    target: "#createButton",
                    content: <OwLabel title={"tutorial_shipments_create_shipment_group"} />,
                });
            }
        }

        steps.push({
            target: ".OwTable .ChevronToggle",
            content: <OwLabel title={"tutorial_shipments_toggle_view_shipments"} />,
        });

        return steps;
    };

    static getDashboardEditorTutorial = (): Step[] => {
        const steps: Step[] = [
            {
                target: "#create",
                content: <OwLabel title={"tutorial_dashboard_editor_create"} />,
            },
            {
                target: "#current-dashboard",
                content: <OwLabel title={"tutorial_dashboard_editor_current_dashboard"} />,
            },
            {
                target: "#edit",
                content: <OwLabel title={"tutorial_dashboard_editor_edit"} />,
            },
            {
                target: "#save",
                content: <OwLabel title={"tutorial_dashboard_editor_save"} />,
                placement: "bottom-end",
            },
            {
                target: "#available-widgets",
                content: <OwLabel title={"tutorial_dashboard_editor_available_widgets"} />,
            },
            {
                target: "#layout-editor",
                content: <OwLabel title={"tutorial_dashboard_editor_layout_editor"} />,
            },
        ];

        return steps;
    };

    static handleShipmentsAutoActions = (data: CallBackProps) => {
        console.log("handleShipmentsAutoActions", data.index);
        if (data.index === 1) {
            // const chevron = document.querySelector('.OwTable .ChevronToggle');
            //
            // if (chevron) {
            //     // @ts-expect-error
            //     chevron.click();
            // }
        }
    };

    static handleTutorialCustomActions = (pathname: string, data: CallBackProps) => {
        switch (pathname) {
            case "/shipments":
                TutorialService.handleShipmentsAutoActions(data);
                break;
        }
    };
}
